import { Link } from "react-router-dom";
import { Package, Heart, Coins, ChevronRight } from "lucide-react";
import { Button } from "@aurelia/ui";
import { useAuthStore } from "../../lib/stores/auth";
import { useLogout } from "../../features/auth/mutations";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { useMyOrders } from "../../features/orders/queries";
import { useWishlist } from "../../features/wishlist/queries";
import { useWallet } from "../../features/wallet/queries";

function StatCard({
  to,
  icon: Icon,
  label,
  value
}: {
  to: string;
  icon: typeof Package;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="rounded-2xl border border-border bg-surface p-5 flex items-center justify-between gap-3 hover:border-accent/40 transition-colors duration-300"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-accent-soft text-accent-strong flex items-center justify-center shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <p className="text-sm text-ink-muted">{label}</p>
          <p className="text-lg font-medium">{value}</p>
        </div>
      </div>
      <ChevronRight size={18} className="text-ink-muted shrink-0" />
    </Link>
  );
}

export function AccountPage() {
  useDocumentMeta("My Account", "Manage your account, orders, wishlist, and wallet.", undefined, undefined, {
    noindex: true
  });
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const { data: orders } = useMyOrders();
  const { data: wishlist } = useWishlist();
  const { data: wallet } = useWallet();

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl border border-border bg-surface-raised p-6 flex flex-col gap-4">
        <div>
          <p className="text-lg font-medium">{user?.fullName}</p>
          <p className="text-ink-muted">{user?.email ?? user?.phone}</p>
          {user?.email && user?.phone && <p className="text-sm text-ink-muted mt-1">{user.phone}</p>}
        </div>
        <Button variant="secondary" className="w-fit" onClick={() => logout.mutate()}>
          Sign out
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard to="/account/orders" icon={Package} label="Orders" value={orders?.total ?? "—"} />
        <StatCard to="/account/wishlist" icon={Heart} label="Wishlist" value={wishlist?.length ?? "—"} />
        <StatCard to="/account/wallet" icon={Coins} label="Wallet" value={wallet ? `${wallet.balance} coins` : "—"} />
      </div>
    </div>
  );
}
